import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  format,
  formatISO,
  getUnixTime,
  parseISO,
// eslint-disable-next-line import/no-duplicates
} from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import { nb } from 'date-fns/locale';
import { arrayMove } from '@dnd-kit/sortable';
import { NewVehicleRegion } from '../types/api/newVehicleRegion';
import { Assignment } from '../types/assignment';
import { MassType } from '../types/massType';
import { Order } from '../types/order';
import { Project } from '../types/project';
import { Staff } from '../types/staff';
import { NewAssignment } from '../types/api/newAssignment';
import { UpdateAssignment } from '../types/api/updateAssignment';
import { NewOrder } from '../types/api/newOrder';
import { Vehicle } from '../types/vehicle';
import { AssignmentStatus } from '../types/enums/assignmentStatus';
import { DateStr, DateTime } from '../types/dateTime';
import { DailySummary } from '../types/dailySummary';
import { UpdateOrder } from '../types/api/updateOrder';
import { ShowPeriod } from './orderFilter';
import { Unavailability } from '../types/unavailability';
import { NewUnavailability } from '../types/api/newUnavailability';
import { RentalVehicle } from '../types/rentalVehicle';
import { NewRentalVehicle } from '../types/api/newRentalVehicle';
import { UpdateRentalVehicle } from '../types/api/updateRentalVehicle';
import { RentalPeriod } from '../types/rentalPeriod';
import { NewRentalPeriod } from '../types/api/newRentalPeriod';
import { UpdateRentalPeriod } from '../types/api/UpdateRentalPeriod';
import { ProjectMRU } from '../../shared/types/projectMRU';
import { Region } from '../types/region';
import { NewSubstituteDriver } from '../types/api/newSubstituteDriver';
import { SubstituteDriver } from '../types/substituteDriver';
import { addAuthHeaders, apiPath } from '../../shared/api';
import { config } from '../../shared/config/transport';
import { Container } from '../types/container';
import { ApproveAllData } from '../types/api/approveAllData';
import { Position } from '../types/api/position';
import { ContainerProjectAssignment } from '../types/containerProjectAssignment';
import { removeUnused } from '../helpers/removeUnusedValues';
import { ContainerPosition } from '../types/containerPosition';
import { UpdateContainerPosition } from '../types/updateContainerPosition';
import { Machine } from '../../shared/types/machine';
import { Certification } from '../../shared/types/certification';
import { SecurityTraining } from '../../shared/types/securityTraining';
import { Company } from '../../shared/types/company';
import { DieselOrder } from '../types/dieselOrder';
import { DieselLoad } from '../types/dieselLoad';
import { DieselLoadUpdate } from '../../Pages/DieselTransportPage/partials/DieselCounter';
import { DragDropData } from '../../Pages/DieselTransportPage/partials/DieselOrderList';
import { DieselDeliveryDay } from '../types/dieselDeliveryDay';
import { CompleteDieselOrder } from '../types/api/completeDieselOrder';
import { DieselDeliveredAmount } from '../types/dieselDeliveredAmount';

function optional(name: string, value: string | null) {
  if (value === null) return '';
  return `&${name}=${value}`;
}

// Define a service using a base URL and expected endpoints
export const transportApi = createApi({
  reducerPath: 'transport',
  baseQuery: fetchBaseQuery({
    baseUrl: apiPath(config, '/transport'),
    prepareHeaders: (h) => addAuthHeaders(config, h), // Adds msal auth headers
  }),
  tagTypes: [
    'assignments',
    'order',
    'orders',
    'vehicles',
    'unavailability',
    'unavailabilities',
    'rentalVehicles',
    'rentalVehicle',
    'projectMRU',
    'regionAssignments',
    'containers',
    'containerProjectAssignments',
    'machine',
    'substitutes',
    'workers',
    'dieselLoad',
    'deliveredDiesel',
    'myCompany',
  ],
  endpoints: (builder) => ({
    getMyCompany: builder.query<Company, void>({
      query: () => ({
        url: '/me/company',
      }),
      providesTags: ['myCompany'],
    }),
    getProjects: builder.query<Project[], void>({
      query: () => '/projects',
    }),
    getStaff: builder.query<Staff[], void>({
      query: () => ({
        url: '/staff',
        params: {
          activeOnly: true,
        },
      }),
    }),
    getMachines: builder.query<Machine[], void>({
      query: () => ({
        url: '/machines',
      }),
      providesTags: ['machine'],
    }),
    getMachine: builder.query<Machine, string>({
      query: (internalNumber) => ({
        url: `/machines/${internalNumber}`,
      }),
      providesTags: ['machine'],
    }),
    getFullProject: builder.query<Project, { id: string; date: string }>({
      query: ({ id, date }) => ({
        url: `/projects/${id}`,
        params: {
          from: date,
        },
      }),
    }),
    getProjectContainers: builder.query<
      Container[],
      | number
      | {
        projectId: number;
        subCategory?: string;
        /** A string that isn't read - used for forcing refresh */
        random?: string;
      }
    >({
      query: (params) => {
        const { projectId, subCategory } = typeof params === 'number'
          ? { projectId: params, subCategory: undefined }
          : params;
        return {
          url: `/projects/${projectId}/containers`,
          params: subCategory ? { subCategory } : undefined,
        };
      },
      keepUnusedDataFor: 0,
      providesTags: ['assignments'],
      transformResponse: (containers: Container[]) => containers
        .sort((a, b) => a.internalNumber.localeCompare(b.internalNumber))
        .sort(
          (a, b) => (a.orderId || a.assignmentId ? 1 : 0)
            - (b.orderId || b.assignmentId ? 1 : 0),
        ),
    }),
    putAssignmentContainer: builder.mutation<
      Assignment,
      {
        id: number;
        loadId: number;
        orderLoadId: number;
        containerNumber: string;
      }
    >({
      query: ({
        id, loadId, orderLoadId, containerNumber,
      }) => ({
        url: `/assignments/${id}/container/${loadId}/assign`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { orderLoadId, containerNumber },
      }),
      invalidatesTags: ['assignments'],
    }),
    putAssignmentContainerTwo: builder.mutation<
      Assignment,
      {
        id: number;
        loadId: number;
        containerNumber: string;
      }
    >({
      query: ({ id, loadId, containerNumber }) => ({
        url: `/assignments/${id}/container/${loadId}/assigntwo`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: `"${containerNumber}"`,
      }),
      invalidatesTags: ['assignments'],
    }),
    putAssignmentExternalContainer: builder.mutation<
      Assignment,
      {
        id: number;
        loadId: number;
        orderLoadId: number;
        containerExternal: string;
      }
    >({
      query: ({
        id, loadId, orderLoadId, containerExternal,
      }) => ({
        url: `/assignments/${id}/container/${loadId}/assign-external`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { orderLoadId, externalId: containerExternal },
      }),
      invalidatesTags: ['assignments'],
    }),
    // "assignments/{id}/container/{loadId}/assign-external
    putContainerWasteType: builder.mutation<
      Assignment,
      {
        id: number;
        loadId: number;
        wasteType: string;
      }
    >({
      query: ({ id, loadId, wasteType }) => ({
        url: `assignments/${id}/container/${loadId}/waste`,
        method: 'PUT',
        params: { wasteType },
      }),
      invalidatesTags: ['assignments'],
    }),
    getMassTypes: builder.query<MassType[], void>({
      query: () => '/masstypes',
    }),
    getAssignments: builder.query<Assignment[], string>({
      query: (date) => ({
        url: '/assignments',
        params: { date },
      }),
      providesTags: ['assignments'],
    }),
    forceRefetchOfAssignments: builder.mutation<null, void>({
      queryFn: () => ({ data: null }),
      invalidatesTags: ['assignments'],
    }),
    forceRefetchOfOrders: builder.mutation<null, void>({
      queryFn: () => ({ data: null }),
      invalidatesTags: ['order'],
    }),
    getOrder: builder.query<Order, string | number>({
      query: (orderId) => `/orders/${orderId}`,
      providesTags: (result, error, id) => [{ type: 'order', id }],
    }),
    getUserOrder: builder.query<Order[], string>({
      query: (date) => `/me/orders?from=${date}`,
    }),
    getUserAssignment: builder.query<Assignment[], DateTime>({
      query: (date) => `/me/assignments?date=${date}`,
    }),
    postCreateAssignment: builder.mutation<void, NewAssignment>({
      query: (assignment) => ({
        url: '/assignments',
        method: 'POST',
        body: removeUnused(assignment),
      }),
      invalidatesTags: (result, error, newAssignment) => [
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
        { type: 'order', id: newAssignment.orderId },
      ],
    }),
    postCreateAssignments: builder.mutation<void, NewAssignment[]>({
      query: (assignments) => ({
        url: '/assignments',
        method: 'POST',
        body: assignments.map((a) => removeUnused(a)),
      }),
      invalidatesTags: (result, error, newAssignments) => [
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
        ...newAssignments.map((a) => ({ type: 'order', id: a.orderId } as { type: 'order', id: number })),
      ],
    }),
    postApproveTodayAssignments: builder.mutation<void, ApproveAllData>({
      query: (body) => ({
        url: '/assignments/approve',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    putAssignmentsBody: builder.mutation<
      void,
      { id: string; data: UpdateAssignment }
    >({
      query: ({ id, data }) => ({
        url: `/assignments/${id}`,
        method: 'PUT',
        body: removeUnused(data),
      }),
    }),
    putAssignmentsStatus: builder.mutation<
      void,
      { id: string; data: AssignmentStatus }
    >({
      query: ({ id, data }) => ({
        url: `/assignments/${id}/status`,
        method: 'PUT',
        params: { status: data },
      }),
    }),
    putAssignmentCompleteContainer: builder.mutation<
      Assignment,
      { id: number; position: Position }
    >({
      query: ({ id, position: body }) => ({
        url: `/assignments/${id}/container/completed`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['assignments', 'containers', 'containerProjectAssignments'],
    }),
    putAssignmentLock: builder.mutation<void, { id: string; state: boolean }>({
      query: ({ id, state }) => ({
        url: `/assignments/${id}/lock`,
        method: 'PUT',
        params: { state },
      }),
    }),
    putOrder: builder.mutation<Order, { id: string; data: UpdateOrder }>({
      query: ({ id, data }) => ({
        url: `/orders/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'order', id },
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
        'assignments',
        'containers',
      ],
    }),
    cancelOrder: builder.mutation<Order, number>({
      query: (id) => ({
        url: `/orders/${id}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'order', id },
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
      ],
    }),
    declineOrder: builder.mutation<Order, number>({
      query: (id) => ({
        url: `/orders/${id}/decline`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'order', id },
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
      ],
    }),
    setOrderPlanned: builder.mutation<Order, number>({
      query: (id) => ({
        url: `/orders/${id}/planned`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'order', id },
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
      ],
    }),
    completeOrder: builder.mutation<Order, number>({
      query: (id) => ({
        url: `/orders/${id}/complete`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'order', id },
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
      ],
    }),
    postOrder: builder.mutation<Order, NewOrder>({
      query: (body) => ({
        url: '/orders',
        method: 'POST',
        body,
      }),
      invalidatesTags: () => [
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
        'projectMRU',
        'containers',
      ],
    }),
    getOrders: builder.query<
      Order[],
      { showPeriod: ShowPeriod; showPlanned: boolean }
    >({
      query: ({ showPeriod, showPlanned }) => {
        if (showPeriod === 'Future') return `/orders?includePlanned=${showPlanned}`;
        const numDays = showPeriod === 'Last7Days' ? 7 : 14;
        return `/orders/last?days=${numDays}`;
      },
      providesTags: (result, error, { showPeriod, showPlanned }) => (showPeriod === 'Future'
        ? [{ type: 'orders', showPlanned }]
        : [{ type: 'orders', showPeriod }]),
    }),
    getDieselOrders: builder.query<DieselDeliveryDay[], ShowPeriod>({
      query: (showPeriod) => {
        if (showPeriod === 'All') return '/diesel';
        if (showPeriod === 'Future') return '/diesel/active';
        const numDays = showPeriod === 'Last7Days' ? 7 : 14;
        return `/diesel/last?days=${numDays}`;
      },
      transformResponse: (orders: DieselOrder[]) => orders
        .reduce((acc: DieselDeliveryDay[], curr): DieselDeliveryDay[] => {
          const date = parseISO(curr.date);
          const day = format(date, 'EEEE do MMM yyyy', { locale: nb });
          const unix = getUnixTime(date);
          const index = acc.findIndex((e) => e.day === day);
          if (index === -1) {
            return [
              ...acc,
              {
                unix,
                day,
                tasks: [curr],
                totals: {
                  Blank: 0,
                  HVO: 0,
                  Colored: 0,
                  [curr.load.dieselType ?? 'total']: curr.load.amount,
                  total: curr.load.amount,
                },
              },
            ];
          }
          const arr = [...acc];

          arr[index].tasks.push(curr);
          arr[index].totals.total += curr.load.amount;
          if (curr.load.dieselType) {
            arr[index].totals[curr.load.dieselType] += curr.load.amount;
          }

          return arr;
        }, [])
        .sort((a, b) => a.unix - b.unix),
      providesTags: (_result, _error, showPeriod) => (showPeriod === 'Future'
        ? [{ type: 'orders' }]
        : [{ type: 'orders' }, { type: 'orders', showPeriod }]),
    }),
    completeDieselOrder: builder.mutation<Order, { id: number; data: CompleteDieselOrder }>({
      query: ({ id, data }) => ({
        url: `/diesel/${id}/complete`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'order', id },
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
        { type: 'deliveredDiesel', id },
        'dieselLoad',
      ],
    }),
    putDieselOrder: builder.mutation<Order, { id: string; data: UpdateOrder }>({
      query: ({ id, data }) => ({
        url: `/diesel/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'order', id },
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
        'dieselLoad',
      ],
    }),
    getDieselDeliveredAmount: builder.query<DieselDeliveredAmount, number>({
      query: (orderId) => `/diesel/delivered-amount/${orderId}`,
      providesTags: (result, error, id) => [{ type: 'deliveredDiesel', id }],
    }),
    getDieselLoad: builder.query<DieselLoad, void>({
      query: () => '/diesel/load',
      providesTags: ['dieselLoad'],
    }),
    updateDieselLoad: builder.mutation<DieselLoad, DieselLoadUpdate>({
      query: ({ body, tank, operation }) => ({
        url: `/diesel/load/${tank}/${operation}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['dieselLoad'],
    }),
    changeDieselOrderPosition: builder.mutation<
      void,
      { showPeriod: ShowPeriod; data: DragDropData }
    >({
      query: ({ data }) => ({
        url: `diesel/change-sort-order/${data.activeOrder}/${data.newIndex}`,
        method: 'PUT',
      }),
      async onQueryStarted(
        { showPeriod, data: { activeDay, activeOrder, overOrder } },
        { dispatch, queryFulfilled },
      ) {
        dispatch(
          transportApi.util.updateQueryData('getDieselOrders', showPeriod, (draft) => draft.map((day) => {
            if (day.day !== activeDay) return day;
            const { tasks } = day;
            const oldIndex = tasks.findIndex((task) => task.id === activeOrder);
            const newIndex = tasks.findIndex((task) => task.id === overOrder);
            return {
              ...day,
              tasks: arrayMove(tasks, oldIndex, newIndex) as DieselOrder[],
            };
          })),
        );
        try {
          await queryFulfilled;
        } catch {
          dispatch(transportApi.util.invalidateTags(['orders']));
        }
      },
    }),
    getVehicles: builder.query<Vehicle[], void | DateTime>({
      query: (d) => ({
        url: '/vehicles',
        params: d ? { date: d } : undefined,
      }),
      providesTags: ['vehicles'],
    }),
    getDayVehicles: builder.query<Vehicle[], DateStr | void>({
      query: (date) => ({
        url: '/vehicles',
        params: {
          date: date ?? formatISO(new Date(), { representation: 'date' }),
        },
      }),
      providesTags: ['regionAssignments', 'vehicles'],
    }),
    getDailySummary: builder.query<DailySummary, string>({
      query: (date) => ({
        url: '/summary',
        params: { date },
      }),
    }),
    getUnavailabilities: builder.query<
      Unavailability[],
      { from: string; to: string | null }
    >({
      query: ({ from, to }) => `/unavailability?from=${from}${optional('to', to)}`,
      providesTags: (result, error, { from, to }) => [
        { type: 'unavailabilities', from, to },
      ],
    }),
    getUnavailability: builder.query<Unavailability, number | string>({
      query: (id) => `/unavailability/${id}`,
      providesTags: (result, error, id) => [
        { type: 'unavailability', id: `${id}` },
      ],
    }),
    getUnavailabilityByVehicleId: builder.query<Unavailability[],
      {
        id: number | string;
        start?: string | undefined;
        end?: string | undefined | null;
      }
      >({
        query: ({ id, start, end }) => ({
          url: `/vehicles/${id}/unavailability/`,
          params: {
            start,
            end,
          },
        }),
        providesTags: (result, error, id) => [
          { type: 'unavailability', id: `${id}` }, 'unavailability',
        ],
      }),
    postUnavailability: builder.mutation<
      Unavailability,
      { id: string; body: NewUnavailability }
    >({
      query: ({ id, body }) => ({
        url: `/vehicles/${id}/unavailability`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['unavailability'],
    }),
    putUnavailability: builder.mutation<
      Unavailability,
      { id: string | number; body: NewUnavailability }
    >({
      query: ({ id, body }) => ({
        url: `/unavailability/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'unavailability', id: `${id}` },
      ],
    }),
    deleteUnavailabilty: builder.mutation<Unavailability, string>({
      query: (id) => ({
        url: `unavailability/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['unavailability'],
    }),
    getRentalVehicles: builder.query<RentalVehicle[], void>({
      query: () => '/rental',
      providesTags: ['rentalVehicles'],
    }),
    getRentalVehicle: builder.query<RentalVehicle, number | string>({
      query: (id) => `/rental/${id}`,
      providesTags: (result, error, id) => [
        { type: 'rentalVehicle', id: `${id}` },
      ],
    }),
    postRentalVehicle: builder.mutation<RentalVehicle, NewRentalVehicle>({
      query: (body) => ({
        url: '/rental',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['rentalVehicles'],
    }),
    putRentalVehicle: builder.mutation<
      RentalVehicle,
      { id: string | number; body: UpdateRentalVehicle }
    >({
      query: ({ id, body }) => ({
        url: `/rental/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'rentalVehicle', id: `${id}` },
        'rentalVehicles',
      ],
    }),
    deleteRentalVehicle: builder.mutation<RentalVehicle, string | number>({
      query: (id) => ({
        url: `rental/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['rentalVehicles'],
    }),
    postRentalPeriod: builder.mutation<RentalPeriod, NewRentalPeriod>({
      query: (body) => ({
        url: '/rental/period',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) => [
        'rentalVehicles',
        ...(result
          ? [
            {
              type: 'rentalVehicle' as const,
              id: `${result.rentalVehicleId}`,
            },
          ]
          : []),
      ],
    }),
    putRentalPeriod: builder.mutation<
      RentalPeriod,
      { id: string | number; body: UpdateRentalPeriod }
    >({
      query: ({ id, body }) => ({
        url: `/rental/period/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result) => [
        'rentalVehicles',
        ...(result
          ? [
            {
              type: 'rentalVehicle' as const,
              id: `${result.rentalVehicleId}`,
            },
          ]
          : []),
      ],
    }),
    deleteRentalPeriod: builder.mutation<
      RentalPeriod,
      { periodId: string | number; vehicleId: string | number }
    >({
      query: ({ periodId }) => ({
        url: `rental/period/${periodId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { vehicleId }) => [
        'rentalVehicles',
        ...(vehicleId
          ? [{ type: 'rentalVehicle' as const, id: `${vehicleId}` }]
          : []),
      ],
    }),
    postMRU: builder.mutation<void, number>({
      query: (body) => ({
        url: '/projects/mru',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['projectMRU'],
    }),
    getProjectMRUs: builder.query<ProjectMRU[], number | void>({
      query: (count) => ({
        url: '/projects/mru',
        params: count ? { count } : {},
      }),
      providesTags: ['projectMRU'],
    }),
    getRegions: builder.query<Region[], number | void>({
      query: () => '/regions',
    }),
    postRegionAssignment: builder.mutation<
      void,
      { id: string; body: NewVehicleRegion }
    >({
      query: ({ id, body }) => ({
        url: `/vehicles/${id}/region`,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['regionAssignments'],
    }),
    getSubstitute: builder.query<
      SubstituteDriver,
      { vehicleId: string; subId: number }
    >({
      query: ({ vehicleId, subId }) => ({
        url: `/vehicles/${vehicleId}/substitutes/${subId}`,
      }),
      providesTags: (x) => ['substitutes', { type: 'substitutes', id: x?.id }],
    }),
    getSubstitutes: builder.query<
      SubstituteDriver[],
      {
        id: string;
        start?: string | undefined;
        end?: string | undefined;
      }
    >({
      query: ({ id, start, end }) => ({
        url: `/vehicles/${id}/substitutes`,
        params: {
          start,
          end,
        },
      }),
      providesTags: ['substitutes'],
    }),
    getSearchSubstitutes: builder.query<
      SubstituteDriver[],
      {
        id: string;
        q: string;
        limit?: number;
      }
    >({
      query: ({ id, q, limit }) => ({
        url: `/vehicles/${id}/substitutes/search`,
        params: {
          q,
          limit,
        },
      }),
    }),
    putSubstitute: builder.mutation<
      SubstituteDriver,
      {
        vehicleId: string;
        subId: number;
        body: NewSubstituteDriver;
      }
    >({
      query: ({ vehicleId, subId, body }) => ({
        url: `/vehicles/${vehicleId}/substitutes/${subId}`,
        method: 'PUT',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (x) => ['vehicles', 'substitutes', { type: 'substitutes', id: x?.id }],
    }),
    postSubstitute: builder.mutation<
      void,
      { id: string; body: NewSubstituteDriver }
    >({
      query: ({ id, body }) => ({
        url: `/vehicles/${id}/substitutes`,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['vehicles', 'substitutes'],
    }),
    deleteSubstitute: builder.mutation<
      SubstituteDriver,
      { vehicleId: string; subId: number }
    >({
      query: ({ vehicleId, subId }) => ({
        url: `vehicles/${vehicleId}/substitutes/${subId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['vehicles'],
    }),
    getAllContainers: builder.query<Container[], { includeAll?: boolean }>({
      query: (params) => ({
        url: '/containers',
        params,
      }),
      providesTags: ['containers'],
    }),
    getContainerTypes: builder.query<string[], void>({
      query: () => '/containers/types',
    }),
    getContainerSubcategories: builder.query<string[], void>({
      query: () => '/containers/subcategories',
    }),
    getContainerWasteTypes: builder.query<string[], void>({
      query: () => '/containers/waste-types',
    }),
    getContainerProjectAssignments: builder.query<
      ContainerProjectAssignment[],
      string
    >({
      query: (internalNumber) => `/containers/${internalNumber}/projects`,
      providesTags: ['containerProjectAssignments'],
    }),
    updateContainerPosition: builder.mutation<
      ContainerPosition,
      {
        internalNumber: string;
        body: UpdateContainerPosition;
      }
    >({
      query: ({ internalNumber, body }) => ({
        url: `/containers/${internalNumber}/position`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['containerProjectAssignments', 'containers'],
    }),
    updateContainerProjectAssignment: builder.mutation<
      ContainerProjectAssignment,
      {
        internalNumber: string;
        uid: string;
        from: DateTime;
        to: DateTime | null;
        comment: string;
        invoiceable: boolean;
      }
    >({
      query: ({
        internalNumber, uid, from, to, comment, invoiceable,
      }) => ({
        url: `/containers/${internalNumber}/projects/${uid}`,
        method: 'PUT',
        body: {
          from,
          to,
          comment,
          invoiceable,
        },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['containerProjectAssignments'],
    }),
    getSingleStaff: builder.query<Staff, number>({
      query: (id) => `/staff/${id}`,
    }),
    getAllCertificationsStaff: builder.query<Certification[], number>({
      query: (id) => `/staff/${id}/certifications`,
    }),
    getAllDocumentedSecurityTraining: builder.query<SecurityTraining[], number>({
      query: (id) => `/staff/${id}/securityTraining`,
    }),
  }),
});

export default transportApi.reducer;

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMyCompanyQuery,
  useGetProjectsQuery,
  useGetStaffQuery,
  useGetMachinesQuery,
  useGetMachineQuery,
  useGetFullProjectQuery,
  useGetProjectContainersQuery,
  usePutAssignmentsBodyMutation,
  usePutAssignmentsStatusMutation,
  usePutAssignmentCompleteContainerMutation,
  usePutOrderMutation,
  useCancelOrderMutation,
  useDeclineOrderMutation,
  useSetOrderPlannedMutation,
  useCompleteOrderMutation,
  useGetMassTypesQuery,
  useGetAssignmentsQuery,
  useGetOrderQuery,
  useGetUserOrderQuery,
  useGetDieselOrdersQuery,
  useGetDieselLoadQuery,
  useCompleteDieselOrderMutation,
  usePutDieselOrderMutation,
  useGetDieselDeliveredAmountQuery,
  useUpdateDieselLoadMutation,
  useChangeDieselOrderPositionMutation,
  useGetUserAssignmentQuery,
  usePostOrderMutation,
  usePostCreateAssignmentsMutation,
  usePostCreateAssignmentMutation,
  usePostApproveTodayAssignmentsMutation,
  useGetVehiclesQuery,
  useGetDayVehiclesQuery,
  useGetOrdersQuery,
  useForceRefetchOfAssignmentsMutation,
  useForceRefetchOfOrdersMutation,
  useGetDailySummaryQuery,
  useGetUnavailabilitiesQuery,
  useGetUnavailabilityQuery,
  usePostUnavailabilityMutation,
  usePutUnavailabilityMutation,
  useDeleteUnavailabiltyMutation,
  useGetRentalVehiclesQuery,
  useGetRentalVehicleQuery,
  usePostRentalVehicleMutation,
  usePutRentalVehicleMutation,
  useDeleteRentalVehicleMutation,
  usePostRentalPeriodMutation,
  usePutRentalPeriodMutation,
  useDeleteRentalPeriodMutation,
  usePostMRUMutation,
  useGetProjectMRUsQuery,
  useGetRegionsQuery,
  usePostRegionAssignmentMutation,
  useGetSubstituteQuery,
  useGetSubstitutesQuery,
  useGetSearchSubstitutesQuery,
  usePutSubstituteMutation,
  usePostSubstituteMutation,
  useDeleteSubstituteMutation,
  useGetAllContainersQuery,
  useGetContainerTypesQuery,
  useGetContainerSubcategoriesQuery,
  useGetContainerWasteTypesQuery,
  usePutAssignmentContainerMutation,
  usePutAssignmentContainerTwoMutation,
  usePutAssignmentExternalContainerMutation,
  usePutContainerWasteTypeMutation,
  useUpdateContainerPositionMutation,
  useGetContainerProjectAssignmentsQuery,
  useUpdateContainerProjectAssignmentMutation,
  useGetSingleStaffQuery,
  useGetAllCertificationsStaffQuery,
  useGetAllDocumentedSecurityTrainingQuery,
  useGetUnavailabilityByVehicleIdQuery,
  util,
} = transportApi;
